import React, { useCallback, useEffect, useState } from "react";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import { physioMeTabletBreakpoint, physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { TextInput } from "@ui/components/inputs/text-input";

interface SearchFilterProps<T> {
	placeholder: string;
	filters?: { label: string; value: T }[];
	onChange: (search: string, activeFilters: T[]) => void;
	defaultActiveFilters?: T[];
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
export const SearchFilter = <T,>({ placeholder, filters, onChange, defaultActiveFilters }: SearchFilterProps<T>): JSX.Element => {
	const { format } = useI18n();
	const [search, setSearch] = useState("");
	const [activeFilters, setActiveFilters] = useState<T[]>(defaultActiveFilters ?? []);

	const toggleFilter = useCallback(
		(value: T) =>
			setActiveFilters(prevFilters =>
				prevFilters.includes(value) ? prevFilters.filter(filter => filter !== value) : [...activeFilters, value],
			),
		[activeFilters],
	);

	useEffect(() => onChange(search, activeFilters), [search, activeFilters, onChange]);

	return (
		<Container>
			<SearchContainer>
				<Row className="p-input-icon-left p-input-icon-right">
					<i className="pi pi-search" />
					<StyledTextInput placeholder={placeholder} value={search} onChange={e => setSearch(e.target.value)} />
					<ClearSearchButton className="pi pi-times" displayed={!!search} onClick={() => setSearch("")} />
				</Row>
			</SearchContainer>
			{filters && (
				<>
					<FiltersContainer>
						<FilterTitle>{format("templateListing.searchFilter.filters")}</FilterTitle>
						<FilterItemContainer>
							{filters.map(filter => (
								<FilterItem
									key={`${filter.label}-${String(filter.value)}`}
									isActive={activeFilters.includes(filter.value)}
									onClick={() => toggleFilter(filter.value)}
								>
									{filter.label}
								</FilterItem>
							))}
						</FilterItemContainer>
					</FiltersContainer>
				</>
			)}
		</Container>
	);
};

const Container = styled.div`
	${row("SM", "flexstart", "center")}

	@media ${physioMeTabletBreakpoint} {
		${stack("XS")}
	}

	@media ${physioMeMobileBreakpoint} {
		width: 100%;
		${stack("XS")}
	}
`;

const FiltersContainer = styled.div`
	${row("XS", "flexstart", "flexstart")}
	
	// I want that each raws have 10px padding top
	margin-top: 10px;
`;

const FilterTitle = styled.div`
  ${row(2, undefined, "center")}

  min-height: 36px;
  white-space: nowrap;
  width: fit-content;
  border-radius: 8px;
  color: ${colors.grey};
`;

const FilterItemContainer = styled.div`
	${row("XS", "flexstart", "center")}
	flex-wrap: wrap;

`;

const FilterItem = styled.div<{ isActive: boolean }>`
	${row(2, undefined, "center")}

	min-height: 36px;
	padding: 0 12px 0 12px;
	cursor: pointer;
	white-space: nowrap;
	width: fit-content;
	border-radius: 8px;
	background-color: ${({ isActive }) => (isActive ? colors.primary10 : "transparent")};
	border: 1px solid ${({ isActive }) => (isActive ? colors.primary : colors.primary25)};
	color: ${({ isActive }) => (isActive ? colors.black : colors.grey)};

	:hover {
		background-color: ${({ isActive }) => (isActive ? colors.primary25 : colors.primaryBackground)};
	}
`;

const SearchContainer = styled.div`
	${row("XS", "flexstart", "center")}
	min-width: 345px;

	@media ${physioMeTabletBreakpoint} {
		min-width: unset;
	}

	@media ${physioMeMobileBreakpoint} {
		width: 100%;
	}
`;

const Row = styled.div`
	flex: 1;
`;

const StyledTextInput = styled(TextInput)`
	width: 100%;
`;

const ClearSearchButton = styled.i<{ displayed: boolean }>`
	cursor: pointer;
	display: ${({ displayed }) => (displayed ? undefined : "none")};

	:hover {
		color: ${colors.primary} !important;
	}
`;